import { getCards } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, ListRow, SectionHeader, StatusBadge } from '@/components/banking';
import { EmptyState, ErrorState, LoadingRegion, RowSkeleton } from '@/components/feedback';
import { CardMark } from '@/features/home/widgets';
import { maskedPan } from '@/services/formatters';

/**
 * Card list.
 *
 * TRACE:XS-SMEM-TT-R049-F5 — every card in the list is clickable and opens that
 * card's detail; there is no card row that looks interactive but does nothing.
 *
 * Lifecycle and fulfilment are shown as separate facts, because the package
 * requires them kept apart: a card can be "active" while its physical delivery
 * is still in progress.
 */
export function CardsPage() {
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const cards = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getCards(companyId, userId)).data;
  }, [companyId, userId]);

  if (!resolved) return null;

  const list = cards.data ?? [];
  const active = list.filter((c) => c.lifecycleStatus === 'active' || c.lifecycleStatus === 'frozen');
  const other = list.filter((c) => !active.includes(c));

  const renderGroup = (title: string, group: typeof list, id: string) =>
    group.length === 0 ? null : (
      <section aria-labelledby={id} key={id}>
        <SectionHeader title={title} id={id} />
        <div className="tm-card tm-card--flush">
          <ul className="tm-rows">
            {group.map((card) => (
              <li key={card.id}>
                <ListRow
                  to={`/mobile/cards/${card.id}`}
                  leading={<CardMark type={card.cardType} />}
                  title={maskedPan(card.last4)}
                  meta={`${card.cardholderName} · ${card.cardType === 'virtual' ? 'Virtual' : 'Physical'}${
                    card.fulfilmentStatus === 'shipped'
                      ? ' · In the post'
                      : card.fulfilmentStatus === 'preparing'
                        ? ' · Being prepared'
                        : ''
                  }`}
                  end={
                    <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                      <StatusBadge status={card.lifecycleStatus} />
                      {card.spendingLimit > 0 ? (
                        <span className="tm-hint">
                          <Amount minorUnits={card.spendingLimit} currency={card.currency} /> limit
                        </span>
                      ) : null}
                    </span>
                  }
                  ariaLabel={`Card ending ${card.last4}, ${card.cardholderName}, ${card.cardType}, ${card.lifecycleStatus}`}
                />
              </li>
            ))}
          </ul>
        </div>
      </section>
    );

  return (
    <>
      <MobileHeader title="Cards" root />
      <div className="tm-stack">
        {cards.status === 'loading' ? (
          <LoadingRegion label="Loading your cards">
            <div className="tm-card tm-card--flush">
              <RowSkeleton count={4} />
            </div>
          </LoadingRegion>
        ) : cards.status === 'error' ? (
          <ErrorState error={cards.error} onRetry={cards.reload} />
        ) : list.length === 0 ? (
          <EmptyState
            title="No cards yet"
            body="When a card is issued to you it will appear here, along with its controls."
          />
        ) : (
          <>
            {renderGroup('Your cards', active, 'cards-active')}
            {renderGroup('Not in use', other, 'cards-other')}
          </>
        )}
      </div>
    </>
  );
}
